import React from "react";

import { I18n } from "i18n-js";
import styled from "styled-components";

import * as locales from "../../../../locales.json";
import { StyledLinkSpan } from "../lib/styles";
import { redesignTheme } from "../../../theme/theme";
import Button from "../../../reusable/button";
import { OpenPortal } from "../lib/types";

const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

const StyledWrapper = styled.div<{ isActive?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: ${redesignTheme.radius.lg};
  background-color: ${(props) =>
    props.isActive ? redesignTheme.color.blue500 : "transparent"};
`;

const StyledActive = styled.div`
  color: white;
  font-weight: ${redesignTheme.fontWeight.semibold};
  min-height: ${redesignTheme.spacing[6]};
  padding-top: ${redesignTheme.spacing[1.5]};
  text-align: center;
`;

const StyledContent = styled.div<{ isActive?: boolean }>`
  background-color: ${redesignTheme.color.gray25};
  border-radius: ${redesignTheme.radius.lg};
  border-color: ${(props) =>
    props.isActive ? redesignTheme.color.blue500 : "white"};
  border-width: ${redesignTheme.spacing[1.5]};
  border-style: solid;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${redesignTheme.spacing[8]} ${redesignTheme.spacing[5]}
    ${redesignTheme.spacing[5]};
  text-align: center;
`;

const StyledTitle = styled.h3`
  color: ${redesignTheme.color.gray900};
  font-family: ${redesignTheme.fontFamily.display};
  font-weight: ${redesignTheme.fontWeight.extrabold};
  margin-bottom: ${redesignTheme.spacing[2.5]};
`;

const StyledSubtitle = styled.p`
  color: ${redesignTheme.color.gray600};
  margin-bottom: ${redesignTheme.spacing[5]};
`;

const StyledDescription = styled.p`
  font-size: ${redesignTheme.fontSize[100]};
  margin-bottom: ${redesignTheme.spacing[2]};
`;

const StyledSecondaryAction = styled.a`
  margin-bottom: ${redesignTheme.spacing[5]};
`;

const StyledListTitle = styled.h4`
  font-family: ${redesignTheme.fontFamily.display};
  font-size: ${redesignTheme.fontSize[50]};
  font-weight: ${redesignTheme.fontWeight.medium};
  margin-bottom: ${redesignTheme.spacing[2]};
  text-align: left;
`;

const StyledList = styled.ul`
  flex: 1;
  font-size: ${redesignTheme.fontSize[1]};
  line-height: 1.875;
  margin-bottom: ${redesignTheme.spacing[8]};
  text-align: left;
`;

const StyledListItem = styled.li`
  list-style-type: disc;
  margin-left: ${redesignTheme.spacing[3.5]};
`;

const StyledButton = styled(Button)`
  border-radius: ${redesignTheme.radius.sm};
`;

const StyledButtonDescription = styled(StyledLinkSpan)`
  color: ${redesignTheme.color.gray500};
  font-size: ${redesignTheme.fontSize[1]};
  min-height: ${redesignTheme.spacing[5]};
  text-align: left;
`;

const REWARDS_COST_LINK =
  "https://help.bonus.ly/en/articles/9275255-paying-for-bonusly-rewards-plans?__hstc=78152587.9820b0ffbd5e6a0f00e10027e4221333.1715282167003.1716581659330.1716585800510.26&__hssc=78152587.2.1716585800510&__hsfp=3620807705";

interface ExplorePlanItemProps {
  title: string;
  description: string;
  price: string;
  plusCost: string;
  featuresTitle: string;
  features: string[];
  isActive: boolean;
  hasPaymentMethod: boolean;
  trialing: boolean;
  isCancelled: boolean;
  openPortal: OpenPortal;
  openCancel: () => void;
  openReactivate: () => void;
  closeModal: () => void;
  setRefetchData: (refetchData: boolean) => void;
}

const ExplorePlansItem = ({
  title,
  description,
  price,
  plusCost,
  featuresTitle,
  features,
  isActive,
  hasPaymentMethod,
  trialing,
  isCancelled,
  openPortal,
  openCancel,
  openReactivate,
  closeModal,
  setRefetchData,
}: ExplorePlanItemProps): React.ReactElement => {
  const primaryVariant = hasPaymentMethod ? "secondary" : "primary";
  const secondaryVariant = hasPaymentMethod ? "primary" : "secondary";
  const cancelText = trialing ? "cancel_trial" : "cancel_plan";
  const addCreditCardText = hasPaymentMethod ? cancelText : "add_credit_card";
  const primaryText = !isCancelled ? addCreditCardText : "reactivate";

  const ctaText = i18n.translate(
    `admin_pages.plans.${isActive ? primaryText : "contact"}`
  );

  const onCancel = (): void => {
    closeModal();
    window.amplitude.track("Admin Billing Cancel Modal Opened");
    openCancel();
  };

  const onSupport = (): void => {
    window.amplitude.track(
      `Admin Billing ${
        isActive ? "Cancel Plan" : "Switch Plan"
      } Support Button Clicked`
    );
    const subject = i18n.translate(
      `admin_pages.plans.${isActive ? "cancel_subject" : "switch_subject"}`
    );
    const body = i18n.translate(
      `admin_pages.plans.${isActive ? "cancel_body" : "switch_body"}`,
      { new_plan_name: title }
    );
    const link = `mailto:support@bonus.ly?subject=${subject}&body=${body}`;
    window.open(link, "_blank");
  };

  const onAddCreditCard = (event?): void => {
    closeModal();
    window.amplitude.track("Admin Billing Add Credit Card Modal Opened");
    return openPortal(event, "PAYMENT_SOURCES", () => {
      return setRefetchData(true);
    });
  };

  const onReactivate = (): void => {
    closeModal();
    window.amplitude.track("Admin Billing Reactivate Modal Opened");
    openReactivate();
  };

  let onClick = onSupport;
  // if it is still in trial we offer self service cancel
  if (isActive && trialing) onClick = onCancel;
  // if plan is cancelled we offer reactivation
  if (isActive && isCancelled) onClick = onReactivate;
  // it is preferred add cc cta if no payment method present or cancelled
  if (isActive && !hasPaymentMethod) onClick = onAddCreditCard;

  return (
    <StyledWrapper isActive={isActive}>
      <StyledActive>
        {isActive && i18n.translate("admin_pages.plans.title")}
      </StyledActive>
      <StyledContent isActive={isActive}>
        <StyledTitle>{title}</StyledTitle>
        <StyledSubtitle>{description}</StyledSubtitle>

        <StyledDescription>{price}</StyledDescription>
        <StyledSecondaryAction
          href={REWARDS_COST_LINK}
          target="_blank"
          rel="noreferrer"
        >
          {plusCost}
        </StyledSecondaryAction>

        <StyledListTitle>{featuresTitle}</StyledListTitle>
        <StyledList>
          {features.map((feature, index) => (
            <StyledListItem key={index}>{feature}</StyledListItem>
          ))}
        </StyledList>

        <StyledButton
          onClick={onClick}
          size="xs"
          variant={isActive ? primaryVariant : secondaryVariant}
          testId="explore-plans-item-button"
        >
          {ctaText}
        </StyledButton>

        <StyledButtonDescription>
          {(!isActive || !trialing) &&
            i18n.translate("admin_pages.plans.support")}
        </StyledButtonDescription>
      </StyledContent>
    </StyledWrapper>
  );
};

export default ExplorePlansItem;
